import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ReferenceKey } from '@1po/1po-bff-fe-spec/generated/catalog/references/common/model/ReferenceKey';
import { ReferenceStock } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceStock';

/* eslint-disable max-len */
import { DelegationBuyer } from '@1po/1po-bff-fe-spec/generated/delegation/response/model/DelegationBuyer';
import {
  OrderDeliveryType,
  OrderMKTPDeliveryMode,
} from '@1po/1po-bff-fe-spec/generated/order/request/CheckoutOrderRequest';
import { Seller } from '@1po/1po-bff-fe-spec/generated/user/model/Seller';
import { TFunction } from 'i18next';
import { v4 as uuidv4 } from 'uuid';
import { ROUTER_CART } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { trackAppEvent } from 'app/AppTracker';
import { useDisclosure } from 'components/Dialog';
import { isBulkCertasOil, isCertasOil } from 'components/StockInfo/catalogStockUtils';
import { useIsBulkOilUser, useIsOilChunkUserR1 } from 'domains/appContext/AppContext.requests';
import { getAllowMktpDeliveryOptions } from 'domains/appContext/AppContext.store';
import {
  useSelectedExternalsReferencesMemo,
  useSelectedOtherReferencesMemo,
  useSelectedVehiclesReferencesMemo,
} from 'domains/basket/Basket.hooks';
import {
  getBasketExadisDeliveryMode,
  getBasketExternalSection,
  getBasketHasSelectedMKTPReference,
  getBasketPrices,
  getBasketVehicles,
  resetReferencesDeliveryType,
  setExadisDeliveryMode,
} from 'domains/basket/Basket.store';
import {
  EXADIS_PICKUP_DELIVERY_TYPE,
  ExternalBasketSectionLocal,
  ReferenceLocal,
  VehicleLocal,
} from 'domains/basket/Basket.types';
import { getActiveSession, getDelegatedBuyer } from 'domains/delegation';
import {
  collectCheckoutExternalReference,
  collectCheckoutOtherSection,
  collectCheckoutVehicleReference,
} from 'domains/order/Order.mapper';
import { checkoutOrderRequestSaga } from 'domains/order/Order.store';
import {
  getDefaultDeliveryAddress,
  getDefaultPaymentMethod,
  getExternalSectionValidation,
  getOrderMKTPDeliveryMode,
  getOtherSectionValidation,
  getVehiclesValidation,
  resetValidationDeliveryAddressSaga,
  resetValidationPaymentMethodSaga,
  setDefaultValidationDeliveryAddressSaga,
  setDefaultValidationPaymentMethodSaga,
  setOrderMKTPDeliveryMode,
} from 'domains/orderValidation';
import { getR1ClientConfig } from 'domains/r1Network/R1Network.store';
import {
  checkIfStocksAreStillUpToDateRequestSaga,
  fetchStocks,
  getStocksMapV2,
  getStockUpdateCheckResult,
  NON_MKTP,
  setReferencesStocksSnapshotData,
} from 'domains/references';
import {
  isBasketRefExadis,
  useAllStocksBasketIAMWithoutMotrioReferences,
  useBasketIAMWithoutMotrioReferencesCount,
} from 'domains/references/References.utils';
import { getCurrency, getUserCommercialLink } from 'domains/user';
import { useFetchBuyersForSellersForDelegationSession } from 'domains/user/User.requests';
import { CART_STEPS_HEIGHT, CartAndValidationStepProps } from 'pages/CartPage/CartPage';
import CardStockValidation from 'pages/CartPage/CartStep/CartStockValidation';
import CheckoutCardSection, { useCheckoutCardSideBySide } from 'pages/CartPage/CartStep/CheckoutCardSection';
import { CheckoutCardDesktopWrapper } from 'pages/CartPage/CartStep/CheckoutCardSection/CheckoutCardSection.styled';
import BillingAccountSection from 'pages/CartPage/ValidationStep/BillingAccountSection';
import DeliveryAddressSection from 'pages/CartPage/ValidationStep/DeliveryAddressSection';
import {
  getStockValidationDecision,
  isCertasReferenceForStockValidation,
} from 'pages/CartPage/ValidationStep/ValidationStep.stockValidation';
import VerificationSection from 'pages/CartPage/ValidationStep/VerificationSection';
import { Flex, MarginBox, StickyBoxCustom, Text } from 'UI';
import { notifyTop } from 'UI/Notification/notification';
import { getData, hasData, isLoading, LOADING, NO_DATA } from 'utils';
import { TRACKING_EVENT_CART_VALIDATION } from 'utils/eventTracker/EventTracker.types';
import { useOffsetTop } from 'utils/hooks/useOffsetTop';
import { useResetScroll } from 'utils/hooks/useResetScroll';
import { ReferenceCardStockValidation } from '../CartStep/CartStockValidation/CardStockValidation';

const getDeliveryType = (t: TFunction) => {
  return {
    OWN_ADDRESS: t('order.validation.address.options.own', 'Delivery to my address'),
    DEALER_PICKUP: t('order.validation.address.options.r1', 'Dealer pick-up'),
    EXADIS_COUNTER_PICKUP: t('order.validation.address.options.exadis_pickup', 'Exadis counter pick-up'),
  };
};

export const getBillingAccountOptions = (dealer: Seller | DelegationBuyer) => {
  const billingOptions = [{ value: dealer.primaryClientCode, title: dealer.primaryClientCode }];
  if (dealer.secondaryClientCodes) {
    dealer.secondaryClientCodes.forEach((code) => billingOptions.push({ value: code, title: code }));
  }
  return billingOptions;
};

const addReferenceToReferenceWithHighestQTYMap = (ref: ReferenceLocal, map: Map<ReferenceKey, ReferenceLocal>) => {
  const key = {
    referenceNumber: ref.referenceNumber,
    sellerNumber: ref.sellerNumber,
  } as ReferenceKey;
  const currValue = map.get(key);
  map.set(key, currValue && currValue.quantity > ref.quantity ? currValue : ref);
};

const getReferenceWithHighestQuantityMap = (
  vehicles: VehicleLocal[],
  otherReferences: ReferenceLocal[],
  selectedExternalsReferences: ExternalBasketSectionLocal[],
) => {
  const map = new Map<ReferenceKey, ReferenceLocal>();

  vehicles.forEach((vehicle) => {
    vehicle.references.forEach((ref) => addReferenceToReferenceWithHighestQTYMap(ref, map));
  });

  otherReferences.forEach((ref) => addReferenceToReferenceWithHighestQTYMap(ref, map));

  selectedExternalsReferences.flatMap((e) => {
    return e.references.forEach((ref) => addReferenceToReferenceWithHighestQTYMap(ref, map));
  });
  return map;
};
const ValidationStep = ({ next }: CartAndValidationStepProps) => {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const vehicles = useSelector(getBasketVehicles);
  const externalBaskets = useSelector(getBasketExternalSection);
  const basketPrices = useSelector(getBasketPrices);
  const currency = useSelector(getCurrency);
  const { totalPriceVatIncluded, totalPriceVatExcluded, totalDiscount } = basketPrices;
  const defaultDeliveryAddress = useSelector(getDefaultDeliveryAddress);
  const defaultPaymentMethod = useSelector(getDefaultPaymentMethod);
  const vehiclesValidation = useSelector(getVehiclesValidation);
  const externalValidation = useSelector(getExternalSectionValidation);
  const otherSectionValidation = useSelector(getOtherSectionValidation);
  const offsetTop = useOffsetTop('CART');
  const activeSession = useSelector(getActiveSession);
  const isDelegationActive = Boolean(activeSession?.active);
  const delegatedBuyer = useSelector(getDelegatedBuyer);
  const commercialLink = useSelector(getUserCommercialLink);
  const activeSeller = commercialLink?.seller ?? null;
  // When connected as a buyer via delegation, use the buyer's billing codes;
  // otherwise use the seller's codes from the commercial link.
  const activeBillingDealer = isDelegationActive && delegatedBuyer ? delegatedBuyer : activeSeller;
  // Ensure buyer payment codes are loaded when a delegation session is active
  useFetchBuyersForSellersForDelegationSession(activeSession?.delegatedSellerTresorCode);
  const xlarge = useCheckoutCardSideBySide();
  const [quantityReferenceValid, setQuantityReferenceValid] = useState<boolean>(false);
  const r1ClientConfig = useSelector(getR1ClientConfig);
  const isDealerPickupEnabled = !!r1ClientConfig.data?.isDealerPickupEnabled;
  const [validationDeliveryAddress, setValidationDeliveryAddress] = useState<OrderDeliveryType>(defaultDeliveryAddress);
  const orderMKTPDeliveryMode = useSelector(getOrderMKTPDeliveryMode);
  const allowMktpDeliveryOptions = useSelector(getAllowMktpDeliveryOptions);
  const basketExadisDeliveryMode = useSelector(getBasketExadisDeliveryMode);
  const basketHasMKTPReference = useSelector(getBasketHasSelectedMKTPReference);
  const [billingAccount, setBillingAccount] = useState(
    (isDelegationActive ? undefined : defaultPaymentMethod) ?? activeBillingDealer?.primaryClientCode ?? '',
  );
  const allStocksBasketIAMWithoutMotrioReferences = useAllStocksBasketIAMWithoutMotrioReferences();
  const stocksR1BasketIAMWithoutMotrioReferences = (
    referenceNumber: string,
    sellerNumber: string | undefined,
  ): ReferenceStock | undefined => {
    const stockData = allStocksBasketIAMWithoutMotrioReferences
      ?.get(referenceNumber)
      ?.data?.get(sellerNumber ?? NON_MKTP);
    if (stockData?.warehouses.some((w) => w.type === 'LOCAL' && w.confirmedQuantity > 0)) {
      return stockData;
    } else {
      return undefined;
    }
  };
  const billingOptions = activeBillingDealer ? getBillingAccountOptions(activeBillingDealer) : undefined;
  const deliveryType = getDeliveryType(t);
  const deliveryOptions = Object.keys(deliveryType)
    .filter((value) => value !== EXADIS_PICKUP_DELIVERY_TYPE)
    .map((option) => {
      return { value: option, title: deliveryType[option as OrderDeliveryType] };
    });
  const [pendingStockCheckId, setPendingStockCheckId] = useState<string | undefined>(undefined);
  const stockUpdateCheckResult = useSelector((state: RootState) =>
    getStockUpdateCheckResult(state, pendingStockCheckId),
  );
  const disclosureStock = useDisclosure();
  const { onOpen: onOpenStockConfirmation, onClose } = disclosureStock;
  const processedStockCheckIdRef = useRef<string | null>(null);

  const selectedVehiclesReferences = useSelectedVehiclesReferencesMemo();
  const selectedExternalsReferences = useSelectedExternalsReferencesMemo();
  const selectedOtherReferences = useSelectedOtherReferencesMemo();

  const allRefsNumbers = [
    ...(selectedExternalsReferences
      .flatMap((v) => v)
      .flatMap((v) => v.references)
      ?.filter((x) => x.origin === 'IAM' || x.referenceSource === 'MARKETPLACE')
      .map((x) => x.referenceNumber) ?? []),
    ...(selectedVehiclesReferences
      .flatMap((v) => v)
      .flatMap((v) => v.references)
      ?.filter((x) => x.origin === 'IAM' || x.referenceSource === 'MARKETPLACE')
      .map((x) => x.referenceNumber) ?? []),
    ...(selectedOtherReferences
      ?.filter((x) => x.origin === 'IAM' || x.referenceSource === 'MARKETPLACE')
      .map((x) => x.referenceNumber) ?? []),
  ];
  const allReferencesStockMapV2 = useSelector((state: RootState) => getStocksMapV2(state, allRefsNumbers ?? []));
  useResetScroll();

  const referenceWithHighestQuantityMap = useMemo(
    () =>
      getReferenceWithHighestQuantityMap(
        selectedVehiclesReferences,
        selectedOtherReferences,
        selectedExternalsReferences,
      ),
    [selectedVehiclesReferences, selectedOtherReferences, selectedExternalsReferences],
  );

  const changedWarehouses: ReferenceCardStockValidation[] | NO_DATA = useMemo(() => {
    if (isLoading(stockUpdateCheckResult)) {
      return LOADING;
    }
    return getData(stockUpdateCheckResult)?.map((stock) => {
      const referenceNumber = stock.reference;
      const key = {
        referenceNumber: stock.reference,
        sellerNumber: stock.sellerNumber,
      } as ReferenceKey;
      const ret: ReferenceCardStockValidation = {
        reference: referenceNumber,
        confirmedQuantity: referenceWithHighestQuantityMap.get(key)?.quantity ?? 1,
        name: referenceWithHighestQuantityMap.get(key)?.name ?? '',
        quantity: referenceWithHighestQuantityMap.get(key)?.quantity ?? 1,
        warehouses: stock.warehouses,
        sellerName: stock.sellerName,
        sellerNumber: stock.sellerNumber,
      };
      return ret;
    });
  }, [referenceWithHighestQuantityMap, stockUpdateCheckResult]);

  const hasAvailableIamOrMarketplaceForRefs = (refs: ReferenceLocal[]): boolean => {
    return refs
      ?.filter((x) => x.origin === 'IAM' || x.referenceSource === 'MARKETPLACE')
      .map((x) => {
        const stockData = allReferencesStockMapV2?.get(x.referenceNumber)?.data;
        if (!stockData) {
          return false;
        }

        const stock = stockData.get(x.sellerNumber ?? NON_MKTP);
        if (!stock && stockData.values.length === 1) {
          return Array.from(stockData.values())[0];
        }
        return (stock?.confirmedQuantity ?? 0) >= x.quantity;
      })
      .every(Boolean);
  };

  const hasAvailableIamOrMarketplaceForExternalOrVehicles = (refs: ExternalBasketSectionLocal[] | VehicleLocal[]) =>
    hasAvailableIamOrMarketplaceForRefs(refs.flat().flatMap((v) => v.references));

  const iamOrMktpReferencesHaveSufficientQuantityAvailable = () =>
    hasAvailableIamOrMarketplaceForRefs(selectedOtherReferences) &&
    hasAvailableIamOrMarketplaceForExternalOrVehicles(selectedExternalsReferences) &&
    hasAvailableIamOrMarketplaceForExternalOrVehicles(selectedVehiclesReferences);

  const handleCheckout = useCallback(() => {
    const referencesByVehicles = collectCheckoutVehicleReference(
      selectedVehiclesReferences,
      vehiclesValidation,
      orderMKTPDeliveryMode,
    );
    const referencesByExternalBasket = collectCheckoutExternalReference(
      selectedExternalsReferences,
      externalValidation,
      orderMKTPDeliveryMode,
    );
    const otherReferences = collectCheckoutOtherSection(
      selectedOtherReferences ?? [],
      otherSectionValidation,
      orderMKTPDeliveryMode,
    );
    dispatch(
      checkoutOrderRequestSaga({
        referencesByVehicles,
        referencesByExternalBasket,
        checkoutOtherSection: otherReferences,
        deliveryType: validationDeliveryAddress,
        paymentClientCode: billingAccount,
      }),
    );
    trackAppEvent(TRACKING_EVENT_CART_VALIDATION);
    next();
  }, [
    billingAccount,
    dispatch,
    validationDeliveryAddress,
    next,
    selectedOtherReferences,
    otherSectionValidation,
    selectedExternalsReferences,
    externalValidation,
    selectedVehiclesReferences,
    vehiclesValidation,
    orderMKTPDeliveryMode,
  ]);

  const hasSufficientQuantityAvailable = iamOrMktpReferencesHaveSufficientQuantityAvailable();
  const isOilUserR1 = useIsOilChunkUserR1();
  const isBulkOilUser = useIsBulkOilUser();

  useEffect(() => {
    setQuantityReferenceValid(hasSufficientQuantityAvailable);

    const decision = getStockValidationDecision({
      pendingStockCheckId,
      hasSufficientQuantityAvailable,
      processedStockCheckId: processedStockCheckIdRef.current ?? undefined,
      isStockCheckResolved: isOilUserR1 || (changedWarehouses !== undefined && !isLoading(changedWarehouses)),
      hasChangedWarehouses: Array.isArray(changedWarehouses) && changedWarehouses.length > 0,
    });
    if (decision === 'WAIT') {
      return;
    }

    processedStockCheckIdRef.current = pendingStockCheckId ?? null;

    if (decision === 'OPEN_CONFIRMATION') {
      onOpenStockConfirmation();
      return;
    }

    setPendingStockCheckId(undefined);
    handleCheckout();
  }, [
    changedWarehouses,
    handleCheckout,
    hasSufficientQuantityAvailable,
    isOilUserR1,
    onOpenStockConfirmation,
    pendingStockCheckId,
  ]);

  useEffect(() => {
    const allClientCodes = [
      activeBillingDealer?.primaryClientCode,
      ...(activeBillingDealer?.secondaryClientCodes ?? []),
    ].filter((code): code is string => code !== undefined);

    if (!allClientCodes.includes(billingAccount)) {
      setBillingAccount(activeBillingDealer?.primaryClientCode ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBillingDealer?.primaryClientCode]);

  useEffect(() => {
    dispatch(resetReferencesDeliveryType());
    return () => updateExadisDeliveryMode(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateStocks = () => {
    if (
      (!selectedVehiclesReferences || selectedVehiclesReferences?.length === 0) &&
      (!selectedOtherReferences || selectedOtherReferences?.length === 0) &&
      (!selectedExternalsReferences || selectedExternalsReferences.length === 0)
    ) {
      notifyTop('error', 'No items for checkout', null);
      return;
    }

    const references = Array.from(referenceWithHighestQuantityMap.values()).map((ref) => {
      return {
        referenceNumber: ref.referenceNumber,
        type: ref.type ?? 'STANDARD',
        quantity: ref.quantity,
        deliveryType: ref.deliveryType,
        sellerNumber: ref.sellerNumber,
        sellerName: ref.sellerName,
        supplierCode: ref.supplierCode,
        origin: ref.origin,
        referenceSource: ref.referenceSource,
        isCertasReference: isCertasReferenceForStockValidation(ref, isBulkOilUser, isOilUserR1),
      };
    });
    const requestId = uuidv4();
    processedStockCheckIdRef.current = null;
    setPendingStockCheckId(requestId);
    dispatch(checkIfStocksAreStillUpToDateRequestSaga({ requestId, references }));
  };

  const resetVerificationPaymentMethods = (
    vehiclesToReset: VehicleLocal[],
    externalsToReset: ExternalBasketSectionLocal[],
  ) => {
    vehiclesToReset.forEach((vehicle) => {
      dispatch(
        resetValidationPaymentMethodSaga({ vehicleKey: vehicle.vehicleDetail.vehicleKey, externalBasketId: undefined }),
      );
    });
    externalsToReset.forEach((ext) => {
      dispatch(resetValidationPaymentMethodSaga({ vehicleKey: undefined, externalBasketId: ext.externalBasketId }));
    });
    dispatch(resetValidationPaymentMethodSaga({ vehicleKey: undefined, externalBasketId: undefined }));
  };

  const resetVerificationDeliveryAddress = (
    vehiclesToReset: VehicleLocal[],
    externalsToReset: ExternalBasketSectionLocal[],
  ) => {
    vehiclesToReset.forEach((vehicle) => {
      dispatch(
        resetValidationDeliveryAddressSaga({
          vehicleKey: vehicle.vehicleDetail.vehicleKey,
          externalBasketId: undefined,
        }),
      );
    });
    externalsToReset.forEach((ext) => {
      dispatch(resetValidationDeliveryAddressSaga({ vehicleKey: undefined, externalBasketId: ext.externalBasketId }));
    });
    dispatch(resetValidationDeliveryAddressSaga({ vehicleKey: undefined, externalBasketId: undefined }));
  };

  const updateDeliveryAddress = (address: OrderDeliveryType) => {
    setValidationDeliveryAddress(address);
    dispatch(setDefaultValidationDeliveryAddressSaga(address));
    resetVerificationDeliveryAddress(vehicles, externalBaskets);
  };

  const updateOrderMKTPDeliveryMode = (mode: OrderMKTPDeliveryMode) => {
    dispatch(setOrderMKTPDeliveryMode(mode));
  };

  const updateExadisDeliveryMode = (isExadisPickUp: boolean) => {
    dispatch(resetReferencesDeliveryType());
    dispatch(setExadisDeliveryMode(isExadisPickUp));
    //take actual snapshot of stocks for diff of red lines/references
    if (isExadisPickUp) {
      dispatch(setReferencesStocksSnapshotData());
    }
    const toStockDetails = (refs: typeof selectedOtherReferences) =>
      refs
        .filter(
          (r) => isBasketRefExadis(r) && !stocksR1BasketIAMWithoutMotrioReferences(r.referenceNumber, r.sellerNumber),
        )
        .map((r) => ({
          referenceNumber: r.referenceNumber,
          type: r.type,
          quantity: r.quantity,
          origin: r.origin,
          supplierCode: r.supplierCode,
          referenceSource: r.referenceSource,
          isPrimaryStockRequest: isExadisPickUp,
          isCertasReference:
            isBulkCertasOil(r.familyCode, r.productModelCode, r.packaging) ||
            (isOilUserR1 && isCertasOil(r.familyCode, r.productModelCode)),
        }));

    const refsOther = toStockDetails(selectedOtherReferences);
    const refsVehicles = selectedVehiclesReferences.flatMap((v) => toStockDetails(v.references));
    const refsExternal = selectedExternalsReferences.flatMap((e) => toStockDetails(e.references));

    dispatch(fetchStocks({ requestStockDetails: [...refsOther, ...refsVehicles, ...refsExternal] }));
  };

  const updateBillingAccount = (account: string) => {
    setBillingAccount(account);
    if (!isDelegationActive) {
      dispatch(setDefaultValidationPaymentMethodSaga(account));
    }
    resetVerificationPaymentMethods(vehicles, externalBaskets);
  };

  const handleCancelStock = () => {
    setPendingStockCheckId(undefined);
    history.push(ROUTER_CART);
    onClose();
  };

  const handleConfirmStock = () => {
    setPendingStockCheckId(undefined);
    handleCheckout();
    onClose();
  };
  const validationEntitiesCount =
    selectedVehiclesReferences.length + (selectedOtherReferences && selectedOtherReferences.length > 0 ? 1 : 0);
  return (
    <Flex direction={'row'} justify={'center'} data-cy={'order-details-page'}>
      <Flex direction={'column'} maxWidth={1600} justify={'center'}>
        {hasData(changedWarehouses) && disclosureStock.isOpen && (
          <CardStockValidation
            isInvalid={!quantityReferenceValid}
            referenceStock={changedWarehouses}
            disclosureStock={disclosureStock}
            handleConfirm={handleConfirmStock}
            handleCancel={handleCancelStock}
          />
        )}
        <Text type={'h1'} data-cy={'validate-your-order-text'}>
          {t('order.validation', 'Validate your order')}
        </Text>
        <MarginBox mt={36} />
        <Flex direction={'row'} align={'flex-start'}>
          <Flex direction={'column'} minWidth={1000}>
            <DeliveryAddressSection
              updateDeliveryAddress={updateDeliveryAddress}
              sellerName={activeSeller?.name}
              validationDeliveryAddress={validationDeliveryAddress}
              basketHasMKTPReference={basketHasMKTPReference}
              updateOrderMKTPDeliveryMode={updateOrderMKTPDeliveryMode}
              orderMKTPDeliveryMode={orderMKTPDeliveryMode}
              updateExadisDeliveryMode={updateExadisDeliveryMode}
              basketIAMReferencesCount={useBasketIAMWithoutMotrioReferencesCount()}
              basketExadisDeliveryMode={basketExadisDeliveryMode}
              isDealerPickupEnabled={isDealerPickupEnabled}
              allowMktpDeliveryOptions={allowMktpDeliveryOptions}
            />
            <BillingAccountSection
              updateBillingAccount={updateBillingAccount}
              billingOptions={billingOptions}
              selectedBillingAccount={billingAccount}
            />
            <VerificationSection
              vehicles={selectedVehiclesReferences}
              billingOptions={billingOptions?.filter((option) => option.value !== billingAccount)}
              deliveryOptions={deliveryOptions?.filter((option) => option.value !== validationDeliveryAddress)}
              externalBaskets={selectedExternalsReferences ?? []}
              otherReferences={selectedOtherReferences}
              allowPaymentChanges={validationEntitiesCount > 1}
              allowAddressChanges={validationEntitiesCount > 1}
              basketExadisDeliveryMode={basketExadisDeliveryMode}
            />
          </Flex>
          {xlarge && (
            <CheckoutCardDesktopWrapper data-cy={'checkout-card-desktop-wrapper'}>
              <StickyBoxCustom offsetTop={offsetTop + CART_STEPS_HEIGHT}>
                <MarginBox ml={30}>
                  <CheckoutCardSection
                    totalPriceVatIncluded={totalPriceVatIncluded ?? 0}
                    totalPriceVatExcluded={totalPriceVatExcluded ?? 0}
                    totalVat={(totalPriceVatIncluded ?? 0) - (totalPriceVatExcluded ?? 0)}
                    currency={currency}
                    onCheckout={validateStocks}
                    loading={isLoading(stockUpdateCheckResult)}
                    vehicles={selectedVehiclesReferences ?? []}
                    externalBaskets={selectedExternalsReferences ?? []}
                    otherReferences={selectedOtherReferences}
                    validationStep
                    totalDiscount={totalDiscount ?? 0}
                  />
                </MarginBox>
              </StickyBoxCustom>
            </CheckoutCardDesktopWrapper>
          )}
        </Flex>
        {!xlarge && (
          <>
            <MarginBox ml={30} />
            <Flex direction={'row'}>
              <Flex />
              <CheckoutCardSection
                totalPriceVatIncluded={totalPriceVatIncluded ?? 0}
                totalPriceVatExcluded={totalPriceVatExcluded ?? 0}
                totalVat={(totalPriceVatIncluded ?? 0) - (totalPriceVatExcluded ?? 0)}
                currency={currency}
                onCheckout={validateStocks}
                loading={isLoading(stockUpdateCheckResult)}
                vehicles={selectedVehiclesReferences ?? []}
                externalBaskets={selectedExternalsReferences ?? []}
                otherReferences={selectedOtherReferences}
                validationStep
                totalDiscount={totalDiscount ?? 0}
              />
              <Flex />
            </Flex>
            <MarginBox mt={30} />
          </>
        )}
      </Flex>
    </Flex>
  );
};

export default ValidationStep;
